import * as styles from './BidMessage.module.scss';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { Eye } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getIsTimedPlusItemBiddingClosed,
    getItemSummary,
    getItemSummaryIsPlacingBid,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { getWonItem } from '@/redux/modules/bidder/biddingInfoAll/bidderBiddingInfoAll.selectors';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { isToday } from 'date-fns';
import { useAppSelector } from '@/redux/hooks';
import DeclineMessage from './DeclineMessage';
import DefaultMessage from './DefaultMessage';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';

export type BidMessageModalOptions = {
    id: string;
    values: { catalogTitle: string } | { lotNumber: string };
};

export type BidMessageProps = {
    itemId: number;
    showSendSellerMessageModal: (options: BidMessageModalOptions) => void;
    showWaitingForApprovalModal: () => void;
};

const BidMessage = ({ itemId, showSendSellerMessageModal, showWaitingForApprovalModal }: BidMessageProps) => {
    const { formatMessage } = useIntl();
    const {
        bidderHasBid,
        bidderHasHighBid,
        bidderWonItem,
        catalogId,
        catalogTitle,
        isAvailable,
        isCatalogOnly,
        isPassed,
        isReserveMet,
        isSold,
        isTimedPlusAuction,
        lotNumber = '',
        saleStartTs,
        sellerName,
    } = useAppSelector((state) => getItemSummary(state, itemId));
    const bidderIsPlacingBid = useAppSelector((state) => getItemSummaryIsPlacingBid(state, itemId));
    const bidderHasWon = useAppSelector((state) => getWonItem(state, itemId)) || bidderWonItem;
    const isTimedPlusItemClosed = useAppSelector((state) => getIsTimedPlusItemBiddingClosed(state, itemId));

    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));
    const { approved, declined, limited, pending, registered } = useAppSelector((state) =>
        getCatalogRegistration(state, catalogId)
    );

    const registrationMessageOptions: BidMessageModalOptions = {
        id: 'registration.issue',
        values: { catalogTitle },
    };
    const paymentMessageOptions: BidMessageModalOptions = {
        id: 'registration.payment_issue',
        values: { lotNumber },
    };

    let bidMessage;
    let bidMessageIcon;
    let secondBidMessage;
    let thirdBidMessage;
    let specialMessage = null;
    let extraClassName = '';
    const isSaleToday = isToday(saleStartTs * 1000);

    if (isAvailable && !isTimedPlusItemClosed) {
        if (isCatalogOnly) {
            bidMessage = 'registration.preview_only';
            bidMessageIcon = (
                <Eye
                    className="mr-2xs inline-block text-surface-500"
                    size="sm"
                    style="fill"
                />
            );
            specialMessage = <FormattedMessage id="registration.preview_only_description" />;
        } else if (declined) {
            bidMessage = 'registration.declined';
            specialMessage = (
                <DeclineMessage
                    sellerName={sellerName}
                    showSendSellerMessageModal={() => {
                        showSendSellerMessageModal(registrationMessageOptions);
                    }}
                />
            );
            extraClassName = styles.declined;
        } else if (pending || (!registered && bidderHasBid)) {
            // second condition if you place bid but didn't register for auction beforehand.  default catalogRegistration
            if (bidderHasBid && bidderHasHighBid && !isReserveMet) {
                bidMessage = 'registration.reserve_not_met';
                extraClassName = styles.closed;
                secondBidMessage = 'registration.reserve_raise_bid';
            } else if (bidderHasBid && bidderHasHighBid && isReserveMet) {
                if (approved) {
                    bidMessage = 'registration.leading_bid';
                    extraClassName = styles.leading;
                    secondBidMessage = 'registration.raise_bid';
                } else {
                    bidMessage = 'registration.leading_after_approval';
                    extraClassName = styles.pending;
                    specialMessage = (
                        <FormattedMessage
                            id="registration.pending_raise_bid"
                            values={{
                                link: (
                                    <InlineButton
                                        key="bid-message-pending-raise-bid-tips"
                                        onClick={showWaitingForApprovalModal}
                                        size="sm"
                                    >
                                        {formatMessage({
                                            id: 'registration.pending_raise_bid_tips',
                                        })}
                                    </InlineButton>
                                ),
                            }}
                        />
                    );
                }
            } else if (bidderHasBid && !bidderHasHighBid && !bidderIsPlacingBid) {
                bidMessage = 'registration.outbid';
                extraClassName = styles.outbid;
                secondBidMessage = 'registration.raise_bid';
            }
        } else if (approved || limited) {
            if (bidderHasBid && bidderHasHighBid && isReserveMet) {
                bidMessage = 'registration.leading_bid';
                extraClassName = styles.leading;
                secondBidMessage = 'registration.raise_bid';
            } else if (bidderHasBid && !bidderHasHighBid && !bidderIsPlacingBid) {
                bidMessage = 'registration.outbid';
                extraClassName = styles.outbid;
                secondBidMessage = 'registration.raise_bid';
            } else if (bidderHasBid && !isReserveMet) {
                bidMessage = 'registration.reserve_not_met';
                extraClassName = styles.closed;
                secondBidMessage = 'registration.falls_below';
                thirdBidMessage = 'registration.reserve_raise_bid';
            }
        }
    } else if (isCatalogOnly && isSaleToday) {
        bidMessage = 'registration.closing_today';
        specialMessage = <FormattedMessage id="registration.preview_only_description_short" />;
    } else if (bidderHasWon && !isPassed) {
        bidMessage = 'registration.lot_won';
        extraClassName = styles.leading;
        specialMessage = (
            <FormattedMessage
                id="registration.lot_won_copy"
                values={{
                    houseName: (
                        <span
                            className={styles['seller-name']}
                            key="bid-message-lot-won-copy-house-name"
                        >
                            {sellerName}
                        </span>
                    ),
                    link: (
                        <InlineButton
                            key="bid-message-lot-won-copy-link"
                            onClick={() => {
                                showSendSellerMessageModal(paymentMessageOptions);
                            }}
                            size="sm"
                        >
                            {formatMessage({
                                id: 'registration.contact_this_auctioneer',
                            })}
                        </InlineButton>
                    ),
                }}
            />
        );
    } else if (bidderHasBid && !bidderHasHighBid && isSold) {
        bidMessage = 'registration.lot_lost';
        extraClassName = styles.outbid;
        specialMessage = (
            <FormattedMessage
                id="registration.lot_lost_copy"
                values={{
                    link: (
                        <Link
                            key="bid-message-lot-lost-copy-link"
                            target="_blank"
                            to="https://help.liveauctioneers.com/article/272-tips-for-winning"
                        >
                            <FormattedMessage id="registration.tips_for_winning" />
                        </Link>
                    ),
                }}
            />
        );
    } else if (isPassed) {
        return null;
    } else {
        bidMessage = 'registration.lot_closed';
        extraClassName = styles.closed;
        secondBidMessage = 'registration.no_longer_bid';
    }

    // TODO: there is logic to show a message for a declined user, but the wrapping clause will not show it.
    // Do we want to show the declined message?
    const shouldShow = (bidderHasBid && (!isLotOpen || isTimedPlusAuction)) || isCatalogOnly;

    return shouldShow ? (
        <div className={styles.register}>
            <div className={styles['register-body']}>
                <div className={styles.bidMessageIconRow}>
                    {bidMessageIcon ? bidMessageIcon : null}
                    {bidMessage && (
                        <DefaultMessage
                            extraClassName={extraClassName}
                            messageId="registration.bid_status"
                            strongMessageId={bidMessage}
                        />
                    )}
                </div>
                <span>
                    {specialMessage}
                    {secondBidMessage && (
                        <BodySecondary color={isReserveMet ? 'primary' : 'secondary'}>
                            <FormattedMessage id={secondBidMessage} />
                        </BodySecondary>
                    )}
                    {thirdBidMessage && (
                        <BodySecondary color={isReserveMet ? 'primary' : 'secondary'}>
                            {' '}
                            <FormattedMessage id={thirdBidMessage} />
                        </BodySecondary>
                    )}
                </span>
            </div>
        </div>
    ) : null;
};

export default BidMessage;
